import type { TariffRing } from '@/services/api/tariffs';

/**
 * Ring auto-suggest (CR4-D5 / CR4b-D12) — a faithful port of logisticdash
 * `src/lib/ring-match.ts`. Pre-fill only: the approver always confirms the
 * ring, so the rule is "suggest only when the answer is CERTAIN":
 *
 *  1. normalise both sides (lowercase, non-alphanumerics → single space)
 *  2. each ring gets its keywords: a city map entry by ring name (with
 *     "Reverse" stripped), else — for a plain city name not in the map — the
 *     name itself; generic names ("Ring 1A", "Normal", "Diskon") get none
 *  3. a ring scores the length of its LONGEST keyword found in the address
 *  4. best score wins; non-Reverse ring preferred on a tie
 *  5. a rival with the same score AND same reverse flag → ambiguous → null
 *  Inactive rings (`active === false`) are skipped before scoring.
 *
 * The backend mirrors this as `suggestRingId`; keep the two in step.
 */

export const normalizeRingText = (v: unknown): string =>
  String(v ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** Address keywords → ring name (lowercase, normalised). Source: ring-match.ts. */
export const RING_KEYWORDS: Record<string, string[]> = {
  jabodetabek: [
    'jakarta', 'dki jakarta', 'jakarta pusat', 'jakarta barat', 'jakarta timur',
    'jakarta selatan', 'jakarta utara', 'bogor', 'depok', 'tangerang',
    'tangerang selatan', 'tangsel', 'bekasi', 'cibubur', 'bintaro', 'serpong',
    'pamulang', 'ciputat', 'cinere', 'cimanggis', 'cikarang',
  ],
  jakarta: ['jakarta', 'dki jakarta'],
  'jakarta area': ['jakarta', 'dki jakarta'],
  bogor: ['bogor', 'cibinong', 'sentul'],
  'bogor cibubur': ['bogor', 'cibubur', 'sentul', 'cibinong'],
  depok: ['depok'],
  'depok bekasi': ['depok', 'bekasi'],
  tangerang: ['tangerang', 'serpong', 'bsd', 'cikupa', 'balaraja'],
  'tangerang selatan': [
    'tangerang selatan', 'tangsel', 'pamulang', 'ciputat', 'bintaro', 'serpong',
    'bsd', 'pondok cabe', 'pd cabe',
  ],
  bekasi: ['bekasi', 'cikarang'],
  cikarang: ['cikarang', 'lippo cikarang', 'jababeka'],
  karawang: ['karawang'],
  serang: ['serang', 'cilegon'],
  bandung: ['bandung', 'cimahi', 'sumedang', 'padalarang', 'soreang'],
  cirebon: ['cirebon', 'kuningan', 'indramayu', 'majalengka'],
  cianjur: ['cianjur'],
  sukabumi: ['sukabumi'],
  garut: ['garut'],
  tasikmalaya: ['tasikmalaya', 'tasik'],
  semarang: ['semarang', 'ungaran', 'kendal', 'demak'],
  solo: ['solo', 'surakarta', 'sukoharjo', 'karanganyar'],
  yogyakarta: ['yogyakarta', 'jogja', 'yogya', 'sleman', 'bantul'],
  surabaya: ['surabaya', 'sidoarjo', 'gresik'],
  malang: ['malang'],
  'metro lampung': ['lampung', 'bandar lampung', 'metro'],
  lampung: ['lampung', 'bandar lampung'],
  palembang: ['palembang'],
  medan: ['medan'],
  makassar: ['makassar'],
  denpasar: ['denpasar', 'bali', 'badung'],
  bali: ['bali', 'denpasar', 'badung'],
};

/** Ring names that can never be guessed from an address. */
const GENERIC_RING_NAMES = ['normal', 'diskon', 'normal reverse'];

/** Keywords for a ring, by its name (see the module doc, step 2). */
export const keywordsForRing = (name: string): string[] => {
  const n = normalizeRingText(name).replace(/\breverse\b/g, '').trim();
  if (!n) return [];
  const mapped = RING_KEYWORDS[n];
  if (mapped) return mapped;
  // Generic names such as "Ring 1A", "Normal", "Diskon" cannot be guessed.
  if (/^ring\b/.test(n) || GENERIC_RING_NAMES.includes(n)) return [];
  // A city name that is not in the map → match on the name itself.
  return [n];
};

/**
 * Suggest a ring id from the destination text (stop name and/or address), or
 * `null` when nothing matches or the best match is ambiguous.
 */
export const suggestRing = (
  rings: TariffRing[],
  destinationName: string
): string | null => {
  const addr = normalizeRingText(destinationName);
  if (!addr || rings.length === 0) return null;

  type Cand = { id: string; score: number; reverse: boolean };
  const cands: Cand[] = [];

  for (const r of rings) {
    // An inactive ring can never be the suggestion (TODO-22).
    if (r.active === false) continue;
    const name = String(r.nama ?? '');
    let best = 0;
    for (const kw of keywordsForRing(name)) {
      const k = normalizeRingText(kw);
      if (!k) continue;
      if (addr.includes(k)) best = Math.max(best, k.length);
    }
    if (best > 0) {
      cands.push({ id: r.id, score: best, reverse: /reverse/i.test(name) });
    }
  }

  if (cands.length === 0) return null;
  // Most specific (longest) keyword first, then the non-Reverse ring.
  cands.sort(
    (a, b) => b.score - a.score || Number(a.reverse) - Number(b.reverse)
  );
  const top = cands[0];
  const rivals = cands.filter(
    (c) => c.id !== top.id && c.score === top.score && c.reverse === top.reverse
  );
  if (rivals.length > 0) return null; // ambiguous → never guess
  return top.id;
};
